import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { environment } from "src/environments/environment";
import { NotificationItemType } from "../models/enums";
import { NotificationItem } from "../models/notification/notification-item";
import { NotificationResponse } from "../models/notification/notification-response";
import { SignalRService } from "./signal-r.service";
import { PopupService } from "./popup.service";
import { buildUrl } from "../shared/url.utils";

/** Service for notification API, list state, and browser system notifications */
@Injectable({
  providedIn: "root",
})
export class NotificationService {
  private readonly notificationSegment = "Notification";
  private readonly notificationSignalRSegment = "notification";
  private readonly setToReadSegment = "SetToRead";

  private _notificationsSource = new BehaviorSubject<NotificationItem[]>([]);
  public readonly notifications = this._notificationsSource.asObservable();

  private openEventCardFromNotification = new Subject<NotificationItem>();

  private readonly headers = {
    "Api-Version": environment.notificationApi.version,
  };

  constructor(
    private httpClient: HttpClient,
    private signalRService: SignalRService,
    private popupService: PopupService
  ) {}

  public getNotifications(): NotificationItem[] {
    return this._notificationsSource.getValue();
  }

  public getOpenEventCardSubject() {
    return this.openEventCardFromNotification;
  }

  public connectToNotificationSignalRService(): void {
    const callbackFunctions: Function[] = [];
    callbackFunctions.push(this.newNotification.bind(this));

    this.signalRService.createConnection(
      buildUrl([
        environment.notificationApi.path.replace("/api", ""),
        this.notificationSignalRSegment,
      ]),
      callbackFunctions,
      environment.config.notificationScope
    );
  }

  public fetchNotifications(): void {
    this.httpClient
      .get<NotificationItem[]>(
        buildUrl([
          environment.notificationApi.path,
          this.notificationSegment,
        ]),
        { headers: this.headers }
      )
      .subscribe({
        next: (notifications) => {
          this._notificationsSource.next(notifications);
        },
        error: (error: HttpErrorResponse) => {
          console.warn("Could not load notifications", error.message);
          this._notificationsSource.next([]);
        },
      });
  }

  public setReadStatus(notificationIds: number[]): void {
    const notifications = this.getNotifications().map((notification) => {
      if (notificationIds.includes(notification.id)) {
        notification.read = true;
      }
      return notification;
    });
    this._notificationsSource.next(notifications);

    this.httpClient
      .post<NotificationResponse>(
        buildUrl([
          environment.notificationApi.path,
          this.notificationSegment,
          this.setToReadSegment,
        ]),
        notificationIds,
        { headers: this.headers }
      )
      .subscribe(
        () => {},
        (error: HttpErrorResponse) => {
          console.error(error);
          this.popupService.setErrorPopup(error);
        }
      );
  }

  /** Ask for permission to send a browser notification, then send it */
  public async askPermissionAndSendBrowserNotification(
    data: NotificationItem,
    logo: string = "/assets/Images/short-logo.png"
  ): Promise<NotificationPermission> {
    return Notification.requestPermission().then(() => {
      this.sendBrowserNotification(data, logo);
      return Notification.permission;
    });
  }

  /** Send a browser system notification */
  public sendBrowserNotification(
    data: NotificationItem,
    logo: string = "/assets/Images/short-logo.png"
  ) {
    const body = this.browserNotificationTextBuilder(data.itemType);
    const notification = new Notification(data.title, {
      body: body ?? undefined,
      icon: logo,
      dir: "auto",
      tag: `${data.id}`,
      lang: "sv-SE",
      data: data,
    });

    notification.onclick = () => {
      this.onClickBrowserNotification(notification.data);
    };
  }

  private newNotification(_id: number): void {
    this.fetchNotifications();
  }

  private onClickBrowserNotification(data: NotificationItem) {
    if (data == null) return;
    this.openEventCardFromNotification.next(data);
  }

  private browserNotificationTextBuilder(type: NotificationItemType) {
    switch (type) {
      case NotificationItemType.Event:
        return "Ny händelse finns nu tillgänglig";
      case NotificationItemType.TrafficAction:
        return "Trafikåtgärd upphör";
      case NotificationItemType.Announcement:
        return "Annonsering nekad";
      default:
        return null;
    }
  }
}
